/**
 * Реестр секций страницы взаимодействий.
 * Дата: 17.03.2026
 */
import type { InteractionEventType } from '@/types/interaction';

export type InteractionSectionId = 'confirmations' | 'feedback' | 'care_followup' | 'birthday' | 'marketing_engagement' | 'account_engagement' | 'all' | 'post_procedure';

export interface InteractionSectionConfig {
  id: InteractionSectionId;
  label: string;
  description: string;
  mode: 'board' | 'table';
  eventTypes?: InteractionEventType[];
  scenarioKind?: string;
  hasAppointment?: boolean;
  emptyDescription: string;
}

// 17.03.2026 - Реестр секций interactions держим отдельно, чтобы страница росла через конфиг.
export const interactionSectionRegistry: InteractionSectionConfig[] = [
  {
    id: 'confirmations',
    label: 'Подтверждения',
    description: 'Подтверждение, отмена и перенос записей.',
    mode: 'board',
    eventTypes: ['appointment_confirm', 'appointment_cancel', 'appointment_reschedule'],
    emptyDescription: 'За выбранный период нет подтверждений, отмен или переносов.',
  },
  {
    id: 'feedback',
    label: 'Сбор ОС',
    description: 'Ответы пациентов по сбору обратной связи.',
    mode: 'board',
    eventTypes: ['feedback_positive', 'feedback_neutral', 'feedback_negative', 'feedback_review_open'],
    emptyDescription: 'За выбранный период нет ответов по сбору обратной связи.',
  },
  {
    id: 'care_followup',
    label: 'После приёма',
    description: 'Переходы по рекомендациям и care follow-up сценариям.',
    mode: 'table',
    eventTypes: ['care_followup_open'],
    emptyDescription: 'За выбранный период нет переходов по сценариям после приёма.',
  },
  {
    id: 'birthday',
    label: 'ДР',
    description: 'Birthday-переходы и выбор подарка по OKDoc/SMS.',
    mode: 'table',
    eventTypes: ['gift_choice'],
    scenarioKind: 'birthday_offer',
    emptyDescription: 'За выбранный период нет birthday-взаимодействий.',
  },
  {
    id: 'marketing_engagement',
    label: 'Маркетинг',
    description: 'Открытия офферов, подарков и маркетинговых CTA.',
    mode: 'table',
    eventTypes: ['promo_open', 'gift_choice', 'offer_claim', 'book_from_landing'],
    emptyDescription: 'За выбранный период нет маркетинговых взаимодействий.',
  },
  {
    id: 'account_engagement',
    label: 'Личный кабинет',
    description: 'Переходы в личный кабинет и contact hub / каналы связи.',
    mode: 'table',
    eventTypes: ['personal_account_open', 'contact_navigation_open'],
    emptyDescription: 'За выбранный период нет переходов в личный кабинет.',
  },
  {
    id: 'post_procedure',
    label: 'После процедур',
    description: 'Кластерные post-procedure ответы и follow-up сценарии.',
    mode: 'table',
    eventTypes: ['cluster_response'],
    emptyDescription: 'За выбранный период нет post-procedure ответов.',
  },
  {
    id: 'all',
    label: 'Все ответы',
    description: 'Полный журнал всех ответов пациентов.',
    mode: 'table',
    emptyDescription: 'За выбранный период нет нажатий кнопок от пациентов.',
  },
];

// getInteractionSectionConfig возвращает конфиг секции по её id.
// Дата: 17.03.2026 - Используется shell/hooks, чтобы страница не держала знания о секциях внутри себя.
export function getInteractionSectionConfig(sectionId: InteractionSectionId): InteractionSectionConfig {
  return interactionSectionRegistry.find((section) => section.id === sectionId) || interactionSectionRegistry[0];
}
